'use client';

import React, { useState, useEffect } from 'react';
import { Panel, Group, Separator } from 'react-resizable-panels';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Check, ChevronDown, RefreshCcw, X, Pencil, GripVertical } from 'lucide-react';
import { ProtectedComponent } from './AuthProvider';
import SearchTypeSelector from '@/components/SearchTypeSelector';
import { useDashboard } from '../hooks/useDashboard';
import UserAccountMenu from './UserAccountMenu';
import { trackDashboardLoad } from '../utils/telemetry';
import { EnvelopeListItem, EnvelopeDetailPanel } from './split-dashboard';
import ReassignmentModal from './ReassignmentModal';
import VoidEnvelopeModal from './VoidEnvelopeModal';
import RemoveDocumentModal from './RemoveDocumentModal';

// MOTD Edit Modal
const MOTDEditModal = ({ isOpen, onClose, currentMotd, onSave, isSubmitting }) => {
  const [message, setMessage] = useState('');
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (isOpen && currentMotd) {
      setMessage(currentMotd.value || '');
      setIsActive(currentMotd.isActive !== false);
    }
  }, [isOpen, currentMotd]);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-xl font-semibold text-gray-900">Edit Announcement</h2>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); onSave(message, isActive); }} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full border border-gray-300 rounded-lg p-3 text-sm resize-none h-32"
                disabled={isSubmitting}
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="motdActive"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="mr-2"
                disabled={isSubmitting}
              />
              <label htmlFor="motdActive" className="text-sm text-gray-700">Show this announcement</label>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={onClose} className="flex-1 bg-gray-100 text-gray-700 hover:bg-gray-200" disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#2db3e1] text-white hover:bg-[#25a0cc]" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

// Mobile Detail Drawer
const MobileDetailDrawer = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl max-h-[85vh] overflow-hidden shadow-xl">
        <div className="sticky top-0 bg-white p-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="font-semibold text-gray-900">Envelope Details</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="overflow-y-auto max-h-[calc(85vh-60px)]">
          {children}
        </div>
      </div>
    </>
  );
};

// Main Split Dashboard Component
const DocuSignDashboardSplit = ({ currentView, onViewChange }) => {
  const {
    filteredEnvelopes, totalItems, searchEmails, searchType, statusFilters, dateFilter,
    customDateRange, currentPage, itemsPerPage, error, loading, isFetching, isPolling, hasSearched,
    isPendingView, awaitingSignatureCount, isAuthLoading,
    userPhoto, userDisplayName, userEmail, isUserInfoLoading, signingInProgress,
    signedEnvelopeId, processingEnvelopeId, signingError, canReassign,
    reassignModalOpen, reassignRecipient, reassignEnvelopeId, isReassigning,
    reassignmentSuccess, sendingReminderKey, reminderSuccess,
    voidModalOpen, voidEnvelope, isVoiding, voidSuccess,
    removeDocModalOpen, removeDocEnvelopeId, removeDocTarget, isRemovingDoc, removeDocSuccess,
    envelopeDocuments, isLoadingDocuments, isLoadingDocView, viewingDocId,
    remindersUpdatingId, remindersSuccess,
    updatesAvailable, motd, motdLoading, isMotdAdmin, motdModalOpen, isSavingMotd, activeSearchParams,
    setSearchType, setStatusFilters, setDateFilter, setCustomDateRange, setActiveSearchParams, setCurrentPage,
    setItemsPerPage, setUpdatesAvailable, setPollingState, setLastUpdateButtonClick,
    setReassignModalOpen, setReassignRecipient, setReassignEnvelopeId, setReassignmentSuccess, setReminderSuccess, setVoidModalOpen, setVoidSuccess,
    setRemoveDocModalOpen, setRemoveDocTarget, setRemoveDocEnvelopeId, setRemoveDocSuccess,
    setMotdModalOpen, setFilteredEnvelopes, setTotalItems, setHasSearched,
    getAccessToken, fetchEnvelopes, handleSearch, handleSearchInput, resetSearch,
    showPendingEnvelopes, toggleEnvelopeView, handleSigningRequest, openReassignModal,
    handleReassign, handleSendReminder, openVoidModal, handleVoidEnvelope,
    fetchEnvelopeDocuments, handleViewDocument, handleSetReminders, openRemoveDocModal, handleRemoveDocument,
    searchTenantUsers, handleSaveMotd, formatDate, hasSequentialSigning,
    handleLogout, debouncedFetch
  } = useDashboard();

  // Track dashboard load once when userEmail is available
  useEffect(() => {
    if (userEmail) {
      trackDashboardLoad(userEmail, currentView);
    }
  }, [userEmail]); // Only fire when userEmail changes (typically once on auth)

  // Selection state
  const [selectedEnvelopeId, setSelectedEnvelopeId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  // Dropdown visibility state (using React state instead of DOM to survive re-renders)
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  // Get selected envelope
  const selectedEnvelope = filteredEnvelopes.find(e => e.envelopeId === selectedEnvelopeId);

  // Clear selection if envelope disappears from list
  useEffect(() => {
    if (selectedEnvelopeId && !filteredEnvelopes.find(e => e.envelopeId === selectedEnvelopeId)) {
      setSelectedEnvelopeId(null);
    }
  }, [filteredEnvelopes, selectedEnvelopeId]);

  // Check viewport for responsive behavior
  useEffect(() => {
    const checkViewport = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    return () => window.removeEventListener('resize', checkViewport);
  }, []);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Date dropdown
      const dateDropdown = document.getElementById('dateDropdownSplit');
      const dateButton = document.getElementById('dateDropdownSplitButton');
      if (dateDropdown && dateButton && !dateDropdown.contains(event.target) && !dateButton.contains(event.target)) {
        setDateDropdownOpen(false);
      }
      // Status dropdown
      const statusDropdown = document.getElementById('statusDropdownSplit');
      const statusButton = document.getElementById('statusDropdownSplitButton');
      if (statusDropdown && statusButton && !statusDropdown.contains(event.target) && !statusButton.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const needsUserSignature = (envelope) => {
    const isActive = ['sent', 'delivered'].includes(envelope.status?.toLowerCase());
    const isNotDeleted = !['deleted', 'voided', 'declined'].includes(envelope.status?.toLowerCase());
    const needsSignature = envelope.recipients?.some(recipient => {
      const emailMatch = recipient.email?.toLowerCase() === userEmail?.toLowerCase();
      const statusMatch = ['sent', 'delivered'].includes(recipient.status?.toLowerCase());
      return emailMatch && statusMatch;
    });
    return isActive && isNotDeleted && needsSignature;
  };

  // Left Panel Content
  const LeftPanelContent = () => (
    <div className="h-full flex flex-col bg-white">
      {/* Search & Filters */}
      <div className="flex-shrink-0 p-4 border-b border-gray-200 space-y-3">
        {/* Search Row */}
        <div className="flex items-center gap-2">
          <SearchTypeSelector searchType={searchType} setSearchType={setSearchType} disabled={loading} />
          <div className="flex-1">
            <Input
              type="text"
              placeholder={searchType === 'email' ? "Search by email" : searchType === 'subject' ? "Search by subject" : searchType === 'name' ? "Search by name" : "Search by envelope ID"}
              value={searchEmails[0]}
              onChange={(e) => handleSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
              className="w-full text-sm"
              disabled={loading}
            />
          </div>
          <Button onClick={handleSearch} className="bg-[#2db3e1] text-white hover:bg-[#25a0cc] px-3" disabled={loading || (isFetching && !isPolling)}>
            {isFetching && !isPolling ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Filter */}
          <div className="relative">
            <Button
              id="dateDropdownSplitButton"
              onClick={() => {
                if (loading) return;
                setDateDropdownOpen(!dateDropdownOpen);
                setStatusDropdownOpen(false);
              }}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs px-2 py-1 h-auto inline-flex items-center"
              disabled={loading}
            >
              {dateFilter === 'custom' ? 'Custom' : dateFilter.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
            <div id="dateDropdownSplit" className="absolute z-50 mt-1 w-48 bg-white rounded-lg shadow-lg border" style={{ display: dateDropdownOpen ? 'block' : 'none' }}>
              <div className="p-2">
                {[['all-time', 'All time'], ['last-12-months', 'Last 12 months'], ['last-6-months', 'Last 6 months'], ['last-30-days', 'Last 30 days'], ['last-week', 'Last week'], ['last-24-hours', 'Last 24 hours']].map(([value, label]) => (
                  <label key={value} className="flex items-center p-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm">
                    <input type="radio" name="dateFilterSplit" value={value} checked={dateFilter === value}
                      onChange={(e) => { setDateFilter(e.target.value); setDateDropdownOpen(false); }}
                      className="mr-2" disabled={loading} />
                    <span>{label}</span>
                  </label>
                ))}
                {/* Custom option with inline date pickers */}
                <div className="border-t mt-1 pt-1">
                  <label className="flex items-center p-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm">
                    <input type="radio" name="dateFilterSplit" value="custom" checked={dateFilter === 'custom'}
                      onChange={() => setDateFilter('custom')}
                      className="mr-2" disabled={loading} />
                    <span>Custom range</span>
                  </label>
                  {dateFilter === 'custom' && (
                    <div className="mt-2 space-y-2 pl-6">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">From</label>
                        <Input type="date" value={customDateRange.from} onChange={(e) => setCustomDateRange(p => ({ ...p, from: e.target.value }))} className="text-xs" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">To</label>
                        <Input type="date" value={customDateRange.to} onChange={(e) => setCustomDateRange(p => ({ ...p, to: e.target.value }))} className="text-xs" />
                      </div>
                      <Button
                        onClick={async () => {
                          setDateDropdownOpen(false);
                          if (hasSearched) await debouncedFetch(() => fetchEnvelopes(true));
                        }}
                        className="w-full bg-[#2db3e1] text-white hover:bg-[#25a0cc] text-xs py-1 h-auto mt-1"
                        disabled={loading}
                      >
                        Apply
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Button
              id="statusDropdownSplitButton"
              onClick={() => {
                if (loading) return;
                setStatusDropdownOpen(!statusDropdownOpen);
                setDateDropdownOpen(false);
              }}
              className="bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs px-2 py-1 h-auto inline-flex items-center"
              disabled={loading}
            >
              {statusFilters[0]?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Status'}
              <ChevronDown className="w-3 h-3 ml-1" />
            </Button>
            <div id="statusDropdownSplit" className="absolute z-50 mt-1 w-40 bg-white rounded-lg shadow-lg border" style={{ display: statusDropdownOpen ? 'block' : 'none' }}>
              <div className="p-2">
                {[['all', 'All'], ['created', 'Draft'], ['in-progress', 'In Progress'], ['completed', 'Completed'], ['declined', 'Declined'], ['deleted', 'Deleted'], ['voided', 'Voided']].map(([value, label]) => (
                  <label key={value} className="flex items-center p-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm">
                    <input type="radio" name="statusFilterSplit" value={value} checked={statusFilters.includes(value)}
                      onChange={(e) => { setStatusFilters([e.target.value]); setStatusDropdownOpen(false); }}
                      className="mr-2" disabled={loading} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Per Page */}
          <select
            className="bg-gray-100 text-gray-700 rounded px-2 py-1 text-xs border-0 cursor-pointer"
            value={itemsPerPage}
            onChange={async (e) => {
              const newValue = Number(e.target.value);
              setItemsPerPage(newValue);
              if (hasSearched) {
                const token = await getAccessToken();
                const params = new URLSearchParams({ pageSize: newValue.toString(), page: '1' });
                // Use activeSearchParams if set (e.g., from "My Envelopes"), otherwise use form state
                const effectiveStatusFilters = activeSearchParams?.statusFilters || statusFilters;
                const effectiveDateFilter = activeSearchParams?.dateFilter || dateFilter;
                const effectiveSearchEmails = activeSearchParams?.searchEmails || searchEmails;
                const effectiveSearchType = activeSearchParams?.searchType || searchType;
                if (!effectiveStatusFilters.includes('all')) {
                  params.append('status', effectiveStatusFilters.flatMap(s => s === 'in-progress' ? ['sent', 'delivered'] : [s]).join(','));
                }
                params.append('dateFilter', effectiveDateFilter);
                effectiveSearchEmails.forEach(t => { if (t.trim()) params.append(effectiveSearchType === 'email' ? 'searchEmails' : effectiveSearchType, t.trim().toLowerCase()); });
                const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/envelopes?${params}`, {
                  headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
                });
                if (res.ok) { const data = await res.json(); setFilteredEnvelopes(data.envelopes); setTotalItems(data.totalCount); setCurrentPage(1); }
              }
            }}
            disabled={loading}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>

          <Button onClick={() => { resetSearch(); setSelectedEnvelopeId(null); }} className="bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs px-2 py-1 h-auto" disabled={loading}>
            Reset
          </Button>

          {/* My Envelopes Button - shows all envelopes where user is a signer */}
          {userEmail && (
            <Button
              onClick={async () => {
                // Perform search directly without populating search box
                const token = await getAccessToken();
                if (!token) return;

                const params = new URLSearchParams({
                  pageSize: itemsPerPage.toString(),
                  page: '1',
                  dateFilter: dateFilter
                });
                params.append('searchEmails', userEmail.toLowerCase());
                // Don't filter by status - show ALL envelopes where user is a recipient

                const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/envelopes?${params}`, {
                  headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
                });
                if (res.ok) {
                  const data = await res.json();
                  // Only update if data actually changed to prevent visual blink
                  const currentIds = filteredEnvelopes.map(e => e.envelopeId).join(',');
                  const newIds = (data.envelopes || []).map(e => e.envelopeId).join(',');
                  if (currentIds !== newIds || totalItems !== data.totalCount) {
                    setFilteredEnvelopes(data.envelopes || []);
                    setTotalItems(data.totalCount || 0);
                  }
                  setCurrentPage(1);
                  setHasSearched(true);
                  // Set activeSearchParams so polling repeats this search
                  setActiveSearchParams({
                    searchEmails: [userEmail.toLowerCase()],
                    searchType: 'email',
                    statusFilters: ['all'],  // Show all statuses
                    dateFilter: dateFilter,
                    customDateRange: { from: '', to: '' }
                  });
                }
              }}
              className="text-xs px-2 py-1 h-auto border border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
              disabled={loading}
            >
              My Envelopes
            </Button>
          )}

          {/* Awaiting Signature Button */}
          {awaitingSignatureCount > 0 && (
            <Button
              onClick={toggleEnvelopeView}
              className={`text-xs px-2 py-1 h-auto transition-colors duration-200 ${
                isPendingView
                  ? 'bg-[#2db3e1] text-white hover:bg-[#25a0cc]'
                  : 'border border-[#2db3e1] text-[#2db3e1] bg-white hover:bg-blue-50'
              }`}
              disabled={loading}
            >
              {isPendingView ? 'Exit Pending View' : `Awaiting Signature (${awaitingSignatureCount})`}
              {isPendingView && <Check className="w-3 h-3 ml-1" />}
            </Button>
          )}
        </div>
      </div>

      {/* Results count + Legend (same row) */}
      <div className="flex-shrink-0 px-4 py-1.5 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className="text-xs text-gray-600">
              {hasSearched && filteredEnvelopes.length > 0 ? (
                <>Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}</>
              ) : hasSearched ? (
                'No results'
              ) : null}
            </p>
            {updatesAvailable && (
              <Button
                onClick={async () => {
                  setLastUpdateButtonClick(Date.now());
                  setPollingState(p => ({ ...p, cooldownUntil: Date.now() + 60000 }));
                  localStorage.removeItem('processingEnvelopes');
                  if (isPendingView) await showPendingEnvelopes(); else await fetchEnvelopes(false);
                  setUpdatesAvailable(false);
                }}
                className="bg-[#2db3e1] text-white hover:bg-[#25a0cc] text-xs px-2 py-1 h-auto"
              >
                <RefreshCcw className="w-3 h-3 mr-1" /> Update
              </Button>
            )}
          </div>
          {/* Status Legend */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-[10px] text-gray-500">Signed</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-sky-500" />
              <span className="text-[10px] text-gray-500">Awaiting</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full bg-white border-2 border-gray-300" />
              <span className="text-[10px] text-gray-500">Pending</span>
            </div>
          </div>
        </div>
      </div>

      {/* Envelope List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">Loading...</div>
        ) : !hasSearched ? (
          null
        ) : filteredEnvelopes.length === 0 && isFetching ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">Searching...</div>
        ) : filteredEnvelopes.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500 text-sm">No documents found</div>
        ) : (
          filteredEnvelopes.map(envelope => (
            <EnvelopeListItem
              key={envelope.envelopeId}
              envelope={envelope}
              isSelected={envelope.envelopeId === selectedEnvelopeId}
              onClick={() => setSelectedEnvelopeId(envelope.envelopeId)}
              needsUserSignature={needsUserSignature(envelope)}
              formatDate={formatDate}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {hasSearched && totalItems > itemsPerPage && (
        <div className="flex-shrink-0 p-3 border-t border-gray-200 flex justify-center gap-2">
          <Button
            onClick={async () => { const p = Math.max(1, currentPage - 1); setCurrentPage(p); await debouncedFetch(() => fetchEnvelopes(false, false, p)); }}
            disabled={currentPage === 1 || loading}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1 h-auto"
          >
            Prev
          </Button>
          <span className="flex items-center text-xs text-gray-600">{currentPage} / {Math.ceil(totalItems / itemsPerPage)}</span>
          <Button
            onClick={async () => { const p = currentPage + 1; setCurrentPage(p); await debouncedFetch(() => fetchEnvelopes(false, false, p)); }}
            disabled={currentPage >= Math.ceil(totalItems / itemsPerPage) || loading}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-xs px-3 py-1 h-auto"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );

  // Right Panel Content
  const RightPanelContent = () => (
    <div className="h-full bg-gray-50">
      <EnvelopeDetailPanel
        envelope={selectedEnvelope}
        userEmail={userEmail}
        canReassign={canReassign}
        signingInProgress={signingInProgress}
        signedEnvelopeId={signedEnvelopeId}
        processingEnvelopeId={processingEnvelopeId}
        handleSigningRequest={handleSigningRequest}
        openReassignModal={openReassignModal}
        handleSendReminder={handleSendReminder}
        sendingReminderKey={sendingReminderKey}
        openVoidModal={openVoidModal}
        formatDate={formatDate}
        hasSequentialSigning={hasSequentialSigning}
        documents={selectedEnvelope ? envelopeDocuments[selectedEnvelope.envelopeId] : null}
        isLoadingDocuments={isLoadingDocuments}
        onFetchDocuments={fetchEnvelopeDocuments}
        onRemoveDocument={openRemoveDocModal}
        onViewDocument={handleViewDocument}
        viewingDocId={viewingDocId}
        onSetReminders={handleSetReminders}
        remindersUpdatingId={remindersUpdatingId}
      />
    </div>
  );

  return (
    <ProtectedComponent>
      {(loading || isUserInfoLoading || !userDisplayName) ? (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-[#2db3e1] text-lg">Loading your dashboard...</div>
        </div>
      ) : (
        <div className="h-screen flex flex-col bg-gray-50">
          {/* Header */}
          <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm">
            <div className="px-6 pt-4 pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl">
                    <span className="font-semibold text-gray-700">Spring</span>{' '}
                    <span className="font-bold text-[#2db3e1]">DocuSpa</span>
                  </h1>
                  <p className="text-sm text-gray-500">
                    Welcome back, {userDisplayName}.{' '}
                    {!isAuthLoading && (
                      <span className="text-gray-600">
                        {awaitingSignatureCount === 0
                          ? "No documents awaiting signature."
                          : awaitingSignatureCount === 1
                            ? "1 document is awaiting your signature."
                            : `${awaitingSignatureCount} documents are awaiting your signature.`}
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <UserAccountMenu
                    userDisplayName={userDisplayName}
                    userEmail={userEmail}
                    userPhoto={userPhoto}
                    handleLogout={handleLogout}
                    currentView={currentView}
                    onViewChange={onViewChange}
                  />
                </div>
              </div>
            </div>

            {/* MOTD */}
            {(motdLoading || (motd?.isActive !== false && motd?.value)) && (
              <div className="px-6 pb-1">
                <div className="px-2 py-1 bg-cyan-50 border border-cyan-200 rounded">
                  {motdLoading ? (
                    <p className="text-xs text-cyan-600">Loading announcement...</p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-cyan-800 flex-1">{motd.value}</p>
                      {isMotdAdmin && (
                        <button onClick={() => setMotdModalOpen(true)} className="p-1 hover:bg-cyan-100 rounded flex-shrink-0">
                          <Pencil className="w-4 h-4 text-cyan-600" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </header>

          {/* Messages */}
          {(error || signingError || reassignmentSuccess || reminderSuccess || voidSuccess || removeDocSuccess) && (
            <div className="flex-shrink-0 px-6 py-2">
              {(error || signingError) && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm mb-2">{error || signingError}</div>
              )}
              {reassignmentSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm flex justify-between items-center mb-2">
                  <span>{reassignmentSuccess.message}</span>
                  <button onClick={() => setReassignmentSuccess(null)}><X className="w-4 h-4" /></button>
                </div>
              )}
              {reminderSuccess && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-sm flex justify-between items-center mb-2">
                  <span>{reminderSuccess.message}</span>
                  <button onClick={() => setReminderSuccess(null)}><X className="w-4 h-4" /></button>
                </div>
              )}
              {voidSuccess && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex justify-between items-center mb-2">
                  <span>{voidSuccess.message}</span>
                  <button onClick={() => setVoidSuccess(null)}><X className="w-4 h-4" /></button>
                </div>
              )}
              {removeDocSuccess && (
                <div className="p-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg text-sm flex justify-between items-center">
                  <span>{removeDocSuccess.message}</span>
                  <button onClick={() => setRemoveDocSuccess(null)}><X className="w-4 h-4" /></button>
                </div>
              )}
            </div>
          )}

          {/* Main Content - Split Panels */}
          <main className="flex-1 overflow-hidden">
            {isMobile ? (
              <>
                {LeftPanelContent()}
                <MobileDetailDrawer
                  isOpen={!!selectedEnvelope}
                  onClose={() => setSelectedEnvelopeId(null)}
                >
                  {RightPanelContent()}
                </MobileDetailDrawer>
              </>
            ) : (
              <Group
                orientation="horizontal"
                id="docuspa-split-layout-v2"
                className="h-full"
              >
                <Panel id="left-panel" defaultSize="65%" minSize="30%" maxSize="80%">
                  {LeftPanelContent()}
                </Panel>
                <Separator className="w-1.5 bg-gray-200 hover:bg-[#2db3e1] active:bg-[#2db3e1] transition-colors cursor-col-resize flex items-center justify-center">
                  <div className="w-1 h-8 rounded bg-gray-400" />
                </Separator>
                <Panel id="right-panel" defaultSize="55%" minSize="30%">
                  {RightPanelContent()}
                </Panel>
              </Group>
            )}
          </main>
        </div>
      )}

      <ReassignmentModal
        isOpen={reassignModalOpen}
        onClose={() => { setReassignModalOpen(false); setReassignRecipient(null); setReassignEnvelopeId(null); }}
        recipient={reassignRecipient}
        envelopeId={reassignEnvelopeId}
        onReassign={(data) => handleReassign(data.newEmail, data.newName, data.justification)}
        isSubmitting={isReassigning}
        searchUsers={searchTenantUsers}
        onViewDocuments={reassignEnvelopeId ? () => handleViewDocument(reassignEnvelopeId, 'combined') : undefined}
      />
      <VoidEnvelopeModal
        isOpen={voidModalOpen}
        onClose={() => { setVoidModalOpen(false); }}
        envelope={voidEnvelope}
        onVoid={handleVoidEnvelope}
        isSubmitting={isVoiding}
      />
      <RemoveDocumentModal
        isOpen={removeDocModalOpen}
        onClose={() => { setRemoveDocModalOpen(false); setRemoveDocTarget(null); setRemoveDocEnvelopeId(null); }}
        document={removeDocTarget}
        envelopeSubject={selectedEnvelope?.subject}
        onRemove={handleRemoveDocument}
        isSubmitting={isRemovingDoc}
      />
      <MOTDEditModal
        isOpen={motdModalOpen}
        onClose={() => setMotdModalOpen(false)}
        currentMotd={motd}
        onSave={handleSaveMotd}
        isSubmitting={isSavingMotd}
      />
    </ProtectedComponent>
  );
};

export default DocuSignDashboardSplit;
